import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Swal from "sweetalert2";
import { registerPushForUser } from "../utils/pushToken";
import { Eye, EyeOff } from "lucide-react";
import { saveUserSession, getSafeNext } from "../utils/session";

const authCss = `
  .as-auth { font-family: 'Hind', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; }
  .as-auth-display { font-family: 'Baloo 2', 'Hind', system-ui, sans-serif; }
`;

const inputCls =
  "w-full rounded-xl border border-[#DAD6F0] bg-white px-4 py-3 text-[16px] text-[#17143F] placeholder:text-slate-400 focus:border-[#3A2E9C] focus:outline-none focus:ring-2 focus:ring-[#3A2E9C]/20 disabled:bg-slate-50";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const next = getSafeNext(location.search); // e.g. /ai-consultancy

  // Users go to ?next=... if given; everyone else goes to their dashboard
  const redirectByRole = (role) => {
    if (role === "user") navigate(next || "/user/dashboard", { replace: true });
    else if (role === "astrologer") navigate("/astrologer/dashboard", { replace: true });
    else if (role === "admin") navigate("/admin/dashboard", { replace: true });
  };

  // Redirect if already logged in
  useEffect(() => {
    const storedUser =
      JSON.parse(sessionStorage.getItem("user")) ||
      JSON.parse(localStorage.getItem("user"));

    if (storedUser?.expiry && Date.now() > storedUser.expiry) {
      localStorage.removeItem("user");
      sessionStorage.removeItem("user");
      return;
    }

    if (storedUser?.role) redirectByRole(storedUser.role);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data } = await axios.post(
        "https://bhavanaastro.onrender.com/api/auth/login",
        { email: email.trim(), password }
      );

      const userData = saveUserSession(data);

      if (userData.role === "astrologer") {
        // Astrologers must get booking alerts, so register before the dashboard opens
        await registerPushForUser(userData);
      } else {
        // Customers: don't make them wait (or see a permission box) before the chat opens
        registerPushForUser(userData);
      }

      redirectByRole(userData.role);
    } catch (err) {
      Swal.fire({
        title: "Login Failed",
        text: err.response?.data?.message || "Invalid credentials",
        icon: "error",
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setLoading(false);
    }
  };

  const signupLink = next
    ? `/user/register?next=${encodeURIComponent(next)}`
    : "/user/register";

  return (
    <div className="as-auth min-h-screen bg-[#F5F4FB] px-4 py-8 sm:py-14">
      <style>{authCss}</style>

      <div className="mx-auto w-full max-w-md">
        <h1 className="as-auth-display text-[29px] font-extrabold leading-tight text-[#17143F]">
          Login karo
        </h1>
        <p className="mt-1 text-[15px] leading-snug text-slate-600">
          {next
            ? "Login karke apni free chat shuru karo."
            : "Apne account mein wapas aao."}
        </p>

        <div className="mt-5 rounded-2xl bg-white p-5 shadow-[0_1px_2px_rgba(23,20,63,0.06),0_10px_24px_-14px_rgba(23,20,63,0.25)]">
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-[#17143F]" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                className={inputCls}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="aap@example.com"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-semibold text-[#17143F]" htmlFor="password">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  className={`${inputCls} pr-12`}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  aria-label={showPw ? "Password chhupao" : "Password dikhao"}
                  className="absolute inset-y-0 right-0 grid w-12 place-items-center text-slate-500"
                >
                  {showPw ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="as-auth-display flex w-full items-center justify-center rounded-2xl bg-[#FFB627] px-6 py-3.5 text-[18px] font-bold text-[#1E1A4D] shadow-[0_10px_28px_-10px_rgba(255,182,39,0.7)] transition hover:bg-[#FFC24D] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? "Login ho raha hai..." : "Login"}
            </button>
          </form>

          <p className="mt-4 text-center text-sm">
            <Link to="/forgot-password" className="text-slate-500 hover:text-[#3A2E9C] hover:underline">
              Password bhool gaye?
            </Link>
          </p>
        </div>

        <p className="mt-4 text-center text-sm text-slate-600">
          Naya account?{" "}
          <Link to={signupLink} className="font-semibold text-[#3A2E9C] underline underline-offset-2">
            Sign up karo
          </Link>
        </p>
      </div>
    </div>
  );
}